import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Label } from './Label';
import { Input } from './Input';
import { Upload, FileJson, AlertCircle, CheckCircle } from 'lucide-react';
import styles from './PersonaImportDialog.module.css';

interface PersonaImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (pomlData: any, importSource?: string) => void;
  isImporting?: boolean;
}

export const PersonaImportDialog: React.FC<PersonaImportDialogProps> = ({
  open,
  onOpenChange,
  onImport,
  isImporting,
}) => {
  const [jsonInput, setJsonInput] = useState('');
  const [importSource, setImportSource] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleJsonChange = (value: string) => {
    setJsonInput(value);
    setParseError(null);
    setParsedData(null);

    if (!value.trim()) return;

    try {
      const parsed = JSON.parse(value);
      // Validate required fields
      if (!parsed.name || !parsed.systemPrompt || !parsed.icon || !parsed.category) {
        setParseError('Missing required fields: name, icon, category, systemPrompt');
        return;
      }
      setParsedData(parsed);
    } catch (e) {
      setParseError('Invalid JSON format');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      handleJsonChange(content);
      setImportSource(file.name);
    };
    reader.readAsText(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!parsedData) return;
    onImport(parsedData, importSource || undefined);
  };

  const handleClose = () => {
    setJsonInput('');
    setImportSource('');
    setParseError(null);
    setParsedData(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={styles.dialog}>
        <DialogHeader>
          <DialogTitle className={styles.titleRow}>
            <Upload size={20} />
            Import POML Persona
          </DialogTitle>
          <DialogDescription>
            Import a persona from a POML JSON file or paste the JSON directly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.uploadSection}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.poml"
              onChange={handleFileUpload}
              className={styles.hiddenInput}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className={styles.uploadButton}
            >
              <FileJson size={16} />
              Choose .poml.json file
            </Button>
            <span className={styles.orText}>or paste JSON below</span>
          </div>

          <div className={styles.field}>
            <Label htmlFor="json">POML JSON</Label>
            <Textarea
              id="json"
              value={jsonInput}
              onChange={(e) => handleJsonChange(e.target.value)}
              placeholder={`{
  "name": "My Persona",
  "description": "Description of the persona",
  "icon": "shield",
  "category": "security",
  "systemPrompt": "Rules and guidelines...",
  "priority": 50,
  "tags": ["tag1", "tag2"]
}`}
              className={styles.textarea}
            />
          </div>

          {parseError && (
            <div className={styles.error}>
              <AlertCircle size={14} />
              {parseError}
            </div>
          )}

          {parsedData && (
            <div className={styles.preview}>
              <CheckCircle size={14} className={styles.checkIcon} />
              <div className={styles.previewInfo}>
                <strong>{parsedData.name}</strong>
                <span className={styles.previewCategory}>{parsedData.category}</span>
              </div>
            </div>
          )}

          <div className={styles.field}>
            <Label htmlFor="source">Import Source (optional)</Label>
            <Input
              id="source"
              value={importSource}
              onChange={(e) => setImportSource(e.target.value)}
              placeholder="E.g., github.com/user/personas"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isImporting || !parsedData}>
              {isImporting ? 'Importing...' : 'Import Persona'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
